/** The two flags a requirements screen needs to render a loading affordance. */
export type RequirementsConfigFetchState = {
  /**
   * Nothing to render yet — the first read of at least one contributing source
   * has not resolved. Callers replace content with a skeleton.
   */
  isLoading: boolean;
  /**
   * Content exists but is being replaced — a refresh after an invalidation.
   * Callers keep the layout and mark the value-bearing parts as refreshing,
   * because the currently rendered value is stale.
   */
  isFetching: boolean;
};

type FetchFlags = {
  isLoading: boolean;
  isFetching: boolean;
};

/**
 * Aggregates the per-type configs' fetch flags with the shared payer-row
 * lookup into the screen-level pair.
 *
 * `isFetching` is tracked separately from `isLoading` on purpose. React Query
 * only reports `isLoading` while a query holds no data, so every refresh that
 * follows a mutation (the whole point of the invalidation fan-out) leaves
 * `isLoading` false — a screen gated solely on it renders pre-mutation data
 * with no indication that a replacement is on the way.
 *
 * The payer-row lookup contributes only when it has ids to resolve: a disabled
 * React Query sits at `status: "loading"` indefinitely, so folding it in
 * unconditionally would pin the screen to a permanent skeleton.
 */
export const selectorRequirementsConfigFetchState = ({
  typeConfigs,
  payerRows
}: {
  typeConfigs: FetchFlags[];
  payerRows: FetchFlags & { isEnabled: boolean };
}): RequirementsConfigFetchState => {
  const isPayerRowsLoading = payerRows.isEnabled && payerRows.isLoading;
  const isPayerRowsFetching = payerRows.isEnabled && payerRows.isFetching;

  const isLoading =
    typeConfigs.some(config => config.isLoading) || isPayerRowsLoading;

  return {
    isLoading,
    // A first load is not a refresh: React Query reports `isFetching` for both,
    // so exclude the initial case or every skeleton would double as a refresh.
    isFetching:
      !isLoading &&
      (typeConfigs.some(config => config.isFetching) || isPayerRowsFetching)
  };
};
